import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
} from "antd";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { updateWorker } from "../services/workersAPI";
import { fetchProjectUsers } from "../services/projectsAPI";
import { useAuth } from "../hooks/useAuth";
import dayjs from "dayjs";

const { TextArea } = Input;

const DATA_SOURCE_OPTIONS = [
  { value: "rfi", label: "RFIs" },
  { value: "submittal", label: "Submittals" },
];

const FREQUENCY_OPTIONS = [
  { value: "once", label: "Once" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const UpdateWorkerModal = ({ visible, onClose, worker }) => {
  const [form] = Form.useForm();
  const { selectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  const { data: users } = useQuery({
    queryKey: ["projectUsers", projectId, 1, 1000],
    queryFn: () => fetchProjectUsers(projectId, 1, 1000),
    enabled: !!projectId,
  });

  useEffect(() => {
    if (worker && visible) {
      form.setFieldsValue({
        ...worker,
        start_date: dayjs(worker.start_date),
        recipients: users?.data
          ?.filter((user) => worker.report_to_emails.includes(user.email))
          .map((user) => user.id || user._id),
      });
      setSelectedUsers(
        users?.data
          ?.filter((user) => worker.report_to_emails.includes(user.email))
          .map((user) => user.id || user._id)
      );
    }
  }, [worker, visible, form, users]);

  const resetForm = () => {
    form.resetFields();
    setSelectedUsers([]);
  };

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateWorker(worker._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["workers", projectId]);
      notification.success({ message: "Worker updated successfully" });
      resetForm();
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update worker",
        description: error.message,
      });
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (values.start_date.isBefore(dayjs())) {
        notification.error({
          message: "Invalid start date",
          description: "Start date cannot be in the past!",
        });
        return;
      }

      const updateData = {
        ...values,
        project_id: projectId,
        report_to_emails: selectedUsers.map(
          (userId) => users?.data?.find((u) => u.id === userId)?.email
        ),
        start_date: values.start_date.format(),
      };
      console.log("Update data:", updateData); // For debugging
      updateMutation.mutate(updateData);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title="Update Worker"
      open={visible}
      onCancel={onClose}
      width={800}
      destroyOnClose={true}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={updateMutation.isPending}
        >
          Update
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please input the worker name!" }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="data_source"
          label="Data Source"
          rules={[
            { required: true, message: "Please select the data source!" },
          ]}
        >
          <Select>
            {DATA_SOURCE_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="prompt"
          label="Worker Prompt"
          rules={[{ required: true, message: "Please input the prompt!" }]}
        >
          <TextArea rows={4} />
        </Form.Item>
        <Form.Item
          name="start_date"
          label="Start Date"
          rules={[
            {
              required: true,
              message: "Please select the start date!",
              type: "object",
            },
            {
              validator: (_, value) => {
                if (value && value.isBefore(dayjs(), "minute")) {
                  return Promise.reject("Start date cannot be in the past!");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            showTime
            disabledDate={(current) => {
              return current && current < dayjs().startOf("day");
            }}
            disabledTime={(current) => {
              if (current && current.isSame(dayjs(), "day")) {
                return {
                  disabledHours: () => Array.from(Array(dayjs().hour()).keys()),
                  disabledMinutes: (selectedHour) => {
                    if (selectedHour === dayjs().hour()) {
                      return Array.from(Array(dayjs().minute()).keys());
                    }
                    return [];
                  },
                };
              }
              return {};
            }}
          />
        </Form.Item>
        <Form.Item
          name="frequency"
          label="Frequency"
          rules={[{ required: true, message: "Please select the frequency!" }]}
        >
          <Select>
            {FREQUENCY_OPTIONS.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="recipients"
          label="Recipients"
          rules={[
            {
              required: true,
              message: "Please select at least one recipient!",
            },
          ]}
        >
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select recipients"
            value={selectedUsers}
            onChange={setSelectedUsers}
            optionFilterProp="label"
            options={users?.data?.map((user) => ({
              value: user.id || user._id,
              label: `${user.username} (${user.email})`,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UpdateWorkerModal;
